import * as React from 'react';

import { Link, DirectLink, Element, Events, animateScroll as scroll, scrollSpy, scroller } from 'react-scroll';

import {List} from '@common/typescript/objects/List';

import Musthead from '@app/components/UI/MustHead/Musthead';
import FoundersBlock from '@app/components/UI/FoundersBlock/FoundersBlock';
import NavTabs from '@app/components/UI/NavTabs/NavTabs';
import FiftyFifty from '@app/components/UI/FiftyFifty/FiftyFifty';
import CreationStorySlider from '@app/components/UI/Slider/CreationStorySlider';
import VideoEmbed from '@app/components/UI/VideoEmbed/VideoEmbed';
import aboutUsDrDani from '@app/content/aboutUsDrDani.jpg';
import aboutUsDrMary from '@app/content/aboutUsDrMary.jpg';
import careCoordinators from '@app/content/coordinators.png';
import BlockRich from '@app/components/UI/LinkBlock/BlockRich';
import {
	blockRichText, creationStorySlidesContent,
	linkMoreCaption, mustHeadText, navItems, videoSrc, videoText, videoTitle, whoBlockText,
	whoBlockTitle,
	whyBlockText, whyBlockTitle
} from '@app/components/Pages/AboutUs/AboutUsContent';
import VetsMap from '@app/components/Pages/AboutUs/VetsMap';
import {rest} from '@app/components/Api';

import {Veterinarian} from '@app/objects/Veterinarian';
import withSeo from '@app/components/LocalCommon/HigherOrderComponents/WithSeo';

import listenerForBrowser from '@app/components/Pages/listenerForBrowser';

import '@app/scss/components/aboutUs.scss';

const dvm360 = require('@app/content/organizations/dvm360.jpg');
const avma = require('@app/content/organizations/avma.gif');
const iaahpc = require('@app/content/organizations/IAAHPC.png');

export interface CreationStorySlide {
	year: string;
	title: string;
	message: string | JSX.Element;
}

const AboutUs = () => {
	const blockRichTitle = (
		<>
			It's a <span className="highlight">calling</span> as much as it is a career
		</>
		);

	const [currentIndex, setCurrentIndex] = React.useState(0);
	const [navCollapse, setNavCollapse] = React.useState(false);

	const [stateName, setStateName] = React.useState('');
	const [veterinarians, setVeterinarians] = React.useState<Veterinarian[]>([]);
	const [vetsCount, setVetsCount] = React.useState(10);

	const whoRef = React.useRef<any>();
	const whyRef = React.useRef<any>();
	const foundersRef = React.useRef<any>();
	const joinRef = React.useRef<any>();
	const organizationsRef = React.useRef<any>();

	const onSetIssue = (index) => {
		const refs = [whoRef, whyRef, foundersRef, joinRef, organizationsRef];

		setCurrentIndex(index);
		refs[index].current.id !== 'founders' ?
			refs[index].current.scrollIntoView({ block: 'center', inline: 'nearest' }) :
			refs[index].current.scrollIntoView({ block: 'start', inline: 'nearest' });
	};

	React.useEffect(() => {
		rest.get<List<Veterinarian>>(`v1/veterinarians/${stateName}?count=${vetsCount}`)
			.then((res) => {
				setVeterinarians(res.list);
			});

	}, [stateName]);

	React.useEffect(() => {
		const mql = window.matchMedia('screen and (max-width: 768px)');

		const listenerFunc = () => {
			if (mql.matches) {
				setNavCollapse(true);
			} else {
				setNavCollapse(false);
			}
		};
		listenerFunc();
		listenerForBrowser(mql, listenerFunc);
	}, []);

	return (
		<div className="about-us">
			<Musthead
				title="About Lap of Love"
				imageSrc={require('@app/content/Dani-and-Mary.jpg')}
				text={mustHeadText}
				quoteAuthor="Lap of Love Hospice Veterinarian"
				imageAlt="Lap of Love veterinarians"
			/>
			<NavTabs
				navItems={navItems}
				currentIndex={currentIndex}
				onSetIssue={onSetIssue}
				navCollapse={navCollapse}
				onChange={onSetIssue}
			/>

			<VetsMap
				veterinarians={veterinarians}
				stateName={stateName}
				setStateName={setStateName}
				setVetsCount={setVetsCount}
			/>

			<FiftyFifty
				title={whoBlockTitle}
				text={whoBlockText}
				src={require('@app/content/whoBlockImg.png')}
				name={navItems[0].name}
				alt="Euthanasia veterinarian"
				refFiftyFifty={whoRef}
			/>
			<CreationStorySlider slides={creationStorySlidesContent} navCollapse={navCollapse} />
			<VideoEmbed
				text={videoText}
				videoSrc={videoSrc}
				videoTitle={videoTitle}
			/>
			<FiftyFifty
				title={whyBlockTitle}
				text={whyBlockText}
				src={require('@app/content/whyBlockImg.png')}
				reversed
				alt="Dr. Mary Gardner"
				name={navItems[1].name}
				refFiftyFifty={whyRef}
			/>
			<FoundersBlock name={navItems[2].name} foundersRef={foundersRef} />
			<BlockRich
				title={blockRichTitle}
				text={blockRichText}
				images={[
					{title: 'Dr. Mary', src: aboutUsDrMary, alt: 'At home euthanasia'},
					{title: 'Dr. Dani', src: aboutUsDrDani, alt: 'Pet hospice',  width: 317},
					{title: 'Care coordinators loving their job!', src: careCoordinators, alt: 'Lap of love support', width: 317},
				]}
				linkMoreCaption={linkMoreCaption}
				subtitle="Join our team"
				name={navItems[3].name}
				refBlockRich={joinRef}
			/>
			<BlockRich title="Proud member of the following organizations" subtitle="Organizations" refBlockRich={organizationsRef} />
			<div className="organizations">
				<div className="organizations-item">
					<a href="https://www.dvm360.com/" target="_blank">
						<img src={dvm360} alt="DVM360"/>
					</a>
				</div>
				<div className="organizations-item">
					<a href="https://www.avma.org/" target="_blank">
						<img src={avma} alt="American Veterinary Medical Association"/>
					</a>
				</div>
				<div className="organizations-item">
					<a href="https://iaahpc.org/" target="_blank">
						<img src={iaahpc} alt="International Association for Animal Hospice and Palliative Care"/>
					</a>
				</div>
			</div>
		</div>
	);
};

export default withSeo(AboutUs, state => state.whoWeArePage, 'whoWeArePage', 'about-us/who-we-are');